import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNotification } from '../contexts/NotificationContext';
import { useAdminTheme } from '../contexts/AdminThemeContext';
import { adminInviteService, AdminInvite, AdminUser } from '../services/adminInviteService';
import { Timestamp, doc, getDoc, getFirestore } from 'firebase/firestore';
import AdminInviteEmail from './AdminInviteEmail';
import { secureEmailService } from '../services/secureEmailService';
import { planPricingService, PricingTier } from '../services/planPricingService';
import QRCode from 'qrcode';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../firebase';

const AdminSettings: React.FC = () => {
  const { currentUser } = useAuth();
  const { showSuccess, showError } = useNotification();
  const { isDark } = useAdminTheme();

  const [settings, setSettings] = useState({
    // General Settings
    siteName: 'Scubamail',
    siteDescription: 'Professional Email Marketing Platform',
    adminEmail: currentUser?.email || '',
    timezone: 'UTC',
    language: 'en',
    adminProfilePicture: '',

    // Email Settings
    smtpHost: '',
    smtpPort: 587,
    smtpUsername: '',
    smtpPassword: '',
    fromEmail: 'info@scubamail.io',
    fromName: 'Scubamail',

    // Security Settings
    twoFactorEnabled: false,
    sessionTimeout: 30,
    passwordMinLength: 8,
    requireStrongPasswords: true,

    // Notification Settings
    emailNotifications: true,
    systemAlerts: true,
    maintenanceMode: false,

    // Appearance Settings
    theme: 'light',
    primaryColor: '#000000',
    logoUrl: '',

    // API Settings
    apiEnabled: true,
    apiRateLimit: 1000,
    webhookUrl: '',
  });

  const [adminInvites, setAdminInvites] = useState<AdminInvite[]>([]);
  const [adminUsers, setAdminUsers] = useState<AdminUser[]>([]);
  const [inviteEmail, setInviteEmail] = useState('');
  const [isInviting, setIsInviting] = useState(false);
  const [showEmailPreview, setShowEmailPreview] = useState(false);
  const [lastInvite, setLastInvite] = useState<AdminInvite | null>(null);
  const [loadingAdminData, setLoadingAdminData] = useState(true);
  const [isSendingInviteEmail, setIsSendingInviteEmail] = useState(false);
  const EMAIL_SEND_TIMEOUT_MS = 10000;

  // Plan Pricing state
  const [planPricing, setPlanPricing] = useState<{ pro: PricingTier[]; business: PricingTier[] }>({
    pro: [],
    business: []
  });
  const [isLoadingPricing, setIsLoadingPricing] = useState(false);
  const [isSavingPricing, setIsSavingPricing] = useState(false);

  // 2FA State
  const [isSettingUp2FA, setIsSettingUp2FA] = useState(false);
  const [qrCodeDataUrl, setQrCodeDataUrl] = useState('');
  const [twoFactorSecret, setTwoFactorSecret] = useState('');
  const [verificationCode, setVerificationCode] = useState('');
  const [setupError, setSetupError] = useState('');
  const [currentUserHas2FA, setCurrentUserHas2FA] = useState(false);

  // Check 2FA Status
  useEffect(() => {
    const check2FAStatus = async () => {
      if (currentUser) {
        const db = getFirestore();
        const userDoc = await getDoc(doc(db, 'users', currentUser.uid));
        if (userDoc.exists()) {
          const data = userDoc.data();
          if (data.adminTwoFactorEnabled) {
            setCurrentUserHas2FA(true);
            handleInputChange('twoFactorEnabled', true);
          }
        }
      }
    };
    check2FAStatus();
  }, [currentUser]);

  const initiate2FASetup = async () => {
    try {
      setSetupError('');
      const generateSecret = httpsCallable(functions, 'generateTwoFactorSecret');
      const result = await generateSecret();
      const data = result.data as { secret: string, otpauth: string };

      setTwoFactorSecret(data.secret);

      // Convert otpauth URL to QR Code Data URL
      const qrUrl = await QRCode.toDataURL(data.otpauth);
      setQrCodeDataUrl(qrUrl);
      setIsSettingUp2FA(true);
    } catch (error) {
      console.error('Error initiating 2FA setup:', error);
      showError('Failed to start 2FA setup.');
    }
  };

  const finalize2FASetup = async () => {
    try {
      const finalizeSetup = httpsCallable(functions, 'finalizeTwoFactorSetup');
      const result = await finalizeSetup({ secret: twoFactorSecret, token: verificationCode });
      const data = result.data as { success: boolean, message?: string };

      if (data.success) {
        showSuccess('2FA enabled successfully!');
        setIsSettingUp2FA(false);
        setCurrentUserHas2FA(true);
        setVerificationCode('');

        // Update local settings state to reflect enabled
        handleInputChange('twoFactorEnabled', true);
      } else {
        setSetupError(data.message || 'Verification failed');
      }
    } catch (error) {
      console.error('Error finalizing 2FA setup:', error);
      setSetupError('Verification failed. Please try again.');
    }
  };

  const disable2FA = async () => {
    if (!window.confirm('Are you sure you want to disable 2FA? This will reduce account security.')) return;

    try {
      const disableAuth = httpsCallable(functions, 'disableTwoFactorAuth');
      await disableAuth();

      showSuccess('2FA disabled.');
      setCurrentUserHas2FA(false);
      handleInputChange('twoFactorEnabled', false);
    } catch (error) {
      console.error('Error disabling 2FA:', error);
      showError('Failed to disable 2FA.');
    }
  };

  // Local Sound Settings
  const [enableSignupSound, setEnableSignupSound] = useState(true);

  useEffect(() => {
    const savedSoundSetting = localStorage.getItem('admin_enableSignupSound');
    if (savedSoundSetting !== null) {
      setEnableSignupSound(savedSoundSetting === 'true');
    }
  }, []);

  const handleToggleSignupSound = (enabled: boolean) => {
    setEnableSignupSound(enabled);
    localStorage.setItem('admin_enableSignupSound', String(enabled));
    window.dispatchEvent(new CustomEvent('notificationSettingsUpdated'));
  };

  // Load settings from Firestore
  useEffect(() => {
    const loadSettings = async () => {
      try {
        const db = getFirestore();
        const settingsDoc = await getDoc(doc(db, 'settings', 'global'));
        if (settingsDoc.exists()) {
          const data = settingsDoc.data();
          setSettings(prev => ({
            ...prev,
            ...data,
            // Ensure session timeout is a number
            sessionTimeout: Number(data.sessionTimeout) || 30
          }));
        }
      } catch (error) {
        console.error('Failed to load settings:', error);
        // Don't show error to user as it might just be first run
      }
    };
    loadSettings();
  }, []);

  useEffect(() => {
    let isMounted = true;

    const loadAdminData = async () => {
      try {
        const [invites, users] = await Promise.all([
          adminInviteService.listInvites(),
          adminInviteService.listAdminUsers(),
        ]);
        if (!isMounted) return;
        setAdminInvites(invites);
        setAdminUsers(users);
      } catch (error) {
        console.error('Failed to load admin invites/users:', error);
        if (isMounted) {
          showError('Failed to load admin invitations. Please refresh.');
        }
      } finally {
        if (isMounted) {
          setLoadingAdminData(false);
        }
      }
    };

    loadAdminData();

    return () => {
      isMounted = false;
    };
  }, [showError]);

  // Load plan pricing
  useEffect(() => {
    const loadPlanPricing = async () => {
      setIsLoadingPricing(true);
      try {
        const pricing = await planPricingService.getPlanPricing();
        setPlanPricing({
          pro: pricing.pro.tiers,
          business: pricing.business.tiers
        });
      } catch (error) {
        console.error('Failed to load plan pricing:', error);
        showError('Failed to load plan pricing');
      } finally {
        setIsLoadingPricing(false);
      }
    };

    loadPlanPricing();
  }, [showError]);

  const formatTimestamp = (value?: Timestamp | Date | null) => {
    if (!value) return '—';
    const date =
      value instanceof Timestamp
        ? value.toDate()
        : value instanceof Date
          ? value
          : new Date(value);
    if (Number.isNaN(date.getTime())) return '—';
    return date.toLocaleDateString();
  };

  const DEFAULT_FROM_EMAIL = 'info@scubamail.io';

  const buildInviteEmailHtml = (invite: AdminInvite, signupUrl: string) => `
    <div style="max-width:600px;margin:0 auto;font-family:Arial,sans-serif;color:#1f2937;">
      <div style="text-align:center;padding:24px 0;">
        <div style="width:64px;height:64px;border-radius:999px;background:#dc2626;margin:0 auto 16px;display:flex;align-items:center;justify-content:center;">
          <span style="font-size:28px;color:#fff;">!</span>
        </div>
        <h1 style="margin:0;font-size:24px;">Admin Invitation</h1>
        <p style="color:#6b7280;">You've been invited to become an admin on Scubamail</p>
      </div>
      <p>Hello,</p>
      <p><strong>${invite.invitedBy}</strong> has invited you to join the Scubamail admin team. Use the button below to accept your invitation and create your admin account.</p>
      <div style="text-align:center;margin:24px 0;">
        <a href="${signupUrl}" style="background:#dc2626;color:#fff;padding:12px 24px;border-radius:8px;text-decoration:none;font-weight:600;display:inline-block;">
          Accept Invitation
        </a>
      </div>
      <p style="font-size:14px;color:#6b7280;">If the button doesn't work, copy and paste this link into your browser:</p>
      <p style="word-break:break-all;font-size:12px;background:#f3f4f6;padding:8px;border-radius:6px;">${signupUrl}</p>
      <div style="margin-top:24px;background:#fef3c7;border:1px solid #fde68a;border-radius:8px;padding:12px;">
        <p style="margin:0 0 6px;font-weight:600;">Important:</p>
        <ul style="margin:0;padding-left:18px;color:#92400e;">
          <li>This invitation expires on ${formatTimestamp(invite.expiresAt)}.</li>
          <li>You must sign up using this email: <strong>${invite.email}</strong>.</li>
          <li>If you weren't expecting this invite, you can ignore it.</li>
        </ul>
      </div>
      <p style="font-size:12px;color:#9ca3af;margin-top:24px;">Sent by ${invite.invitedBy} via Scubamail Admin Console.</p>
    </div>
  `;

  const buildInviteEmailText = (invite: AdminInvite, signupUrl: string) => `
Hello,

${invite.invitedBy} has invited you to become an administrator on Scubamail.

Accept your invitation by visiting:
${signupUrl}

This invitation expires on ${formatTimestamp(invite.expiresAt)} and must be used with the email ${invite.email}.

If you didn't expect this invitation, you can ignore this email.

— Scubamail Team
`;

  const sendInviteEmail = async (invite: AdminInvite, recipientEmail: string, signupUrl: string) => {
    try {
      setIsSendingInviteEmail(true);

      const emailResult = await secureEmailService.sendEmail({
        from: {
          email: settings.fromEmail || DEFAULT_FROM_EMAIL,
          name: settings.siteName || 'Scubamail',
        },
        to: [{ email: recipientEmail }],
        subject: 'You have been invited to become a Scubamail admin',
        htmlBody: buildInviteEmailHtml(invite, signupUrl),
        textBody: buildInviteEmailText(invite, signupUrl),
      });

      if (!emailResult.success) {
        throw new Error(emailResult.error || 'Failed to send invite email.');
      }

      showSuccess(`Invitation email sent to ${recipientEmail}.`);
    } catch (emailError: any) {
      console.error('Admin invite email failed:', emailError);
      const rawMessage = emailError?.message || emailError?.error || 'Invite email could not be sent.';
      const needsVerification = /not verified/i.test(rawMessage) || /identit[y|ies] failed/i.test(rawMessage);
      const friendlyMessage = needsVerification
        ? 'Email service rejected the sender. Please verify the “From” address in AWS SES (region ap-south-1) or update the Admin “From Email” setting to a verified address. The invite link is still available below.'
        : `${rawMessage} The invite link is still available below.`;
      showError(friendlyMessage);
    } finally {
      setIsSendingInviteEmail(false);
    }
  };


  const [activeSection, setActiveSection] = useState('general');
  const [isSaving, setIsSaving] = useState(false);
  const [isUploadingImage, setIsUploadingImage] = useState(false);
  const [imagePreview, setImagePreview] = useState<string | null>(null);

  // Load admin profile picture from localStorage on component mount
  useEffect(() => {
    const savedProfilePicture = localStorage.getItem('adminProfilePicture');
    if (savedProfilePicture) {
      setSettings(prev => ({
        ...prev,
        adminProfilePicture: savedProfilePicture
      }));
    }
  }, []);

  const handleInputChange = (field: string, value: any) => {
    setSettings(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleImageUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      showError('Please select a valid image file');
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      showError('Image size must be less than 5MB');
      return;
    }

    setIsUploadingImage(true);

    try {
      // Create preview URL
      const previewUrl = URL.createObjectURL(file);
      setImagePreview(previewUrl);

      // Convert file to data URL for persistence
      const reader = new FileReader();
      reader.onload = (e) => {
        const dataUrl = e.target?.result as string;

        // Save to localStorage for persistence
        localStorage.setItem('adminProfilePicture', dataUrl);

        // Dispatch custom event to update header
        window.dispatchEvent(new CustomEvent('profilePictureUpdated'));

        handleInputChange('adminProfilePicture', dataUrl);
        showSuccess('Profile picture uploaded successfully!');
      };

      reader.readAsDataURL(file);

      // Simulate upload to server (in real implementation, upload to your storage service)
      await new Promise(resolve => setTimeout(resolve, 2000));
    } catch (error) {
      showError('Failed to upload image. Please try again.');
      setImagePreview(null);
    } finally {
      setIsUploadingImage(false);
    }
  };

  const handleRemoveImage = () => {
    setImagePreview(null);
    localStorage.removeItem('adminProfilePicture');

    // Dispatch custom event to update header
    window.dispatchEvent(new CustomEvent('profilePictureUpdated'));

    handleInputChange('adminProfilePicture', '');
    showSuccess('Profile picture removed');
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      const db = getFirestore();
      await import('firebase/firestore').then(({ setDoc, doc }) =>
        setDoc(doc(db, 'settings', 'global'), settings, { merge: true })
      );
      showSuccess('Settings saved successfully!');
    } catch (error) {
      console.error('Error saving settings:', error);
      showError('Failed to save settings');
    } finally {
      setIsSaving(false);
    }
  };

  const handleInviteAdmin = async () => {
    if (!inviteEmail.trim()) {
      showError('Please enter an email address');
      return;
    }

    if (!/\S+@\S+\.\S+/.test(inviteEmail)) {
      showError('Please enter a valid email address');
      return;
    }

    // Check if email is already an admin
    if (await adminInviteService.isEmailAdmin(inviteEmail)) {
      showError('This email is already an admin');
      return;
    }

    // Check if there's already a pending invite
    const normalizedEmail = inviteEmail.toLowerCase();
    const existingInvite = adminInvites.find(
      (inv) => inv.email === normalizedEmail && inv.status === 'pending'
    );
    if (existingInvite) {
      showError('A pending invite already exists for this email');
      return;
    }

    setIsInviting(true);
    setIsSendingInviteEmail(false);
    try {
      // Create admin invite
      const newInvite = await adminInviteService.createInvite(
        inviteEmail,
        currentUser?.email || 'Unknown'
      );

      const signupUrl = `${window.location.origin}/admin/signup?invite=${newInvite.inviteToken}&email=${encodeURIComponent(inviteEmail)}`;
      console.log('Admin invite link generated:', signupUrl);

      await sendInviteEmail(newInvite, inviteEmail, signupUrl);

      setAdminInvites(prev => {
        const filtered = prev.filter(inv => inv.id !== newInvite.id);
        return [newInvite, ...filtered];
      });
      setLastInvite(newInvite);
      setShowEmailPreview(true);
      setInviteEmail('');
    } catch (error) {
      showError('Failed to send admin invite');
    } finally {
      setIsInviting(false);
    }
  };

  const handleResendInvite = async (inviteId: string) => {
    try {
      const invite = adminInvites.find(inv => inv.id === inviteId);
      if (!invite) {
        showError('Invite not found');
        return;
      }

      // Simulate resend (hook up to real email service)
      await new Promise(resolve => setTimeout(resolve, 500));
      const signupUrl = `${window.location.origin}/admin/signup?invite=${invite.inviteToken}&email=${encodeURIComponent(invite.email)}`;
      console.log('Admin invite resent with URL:', signupUrl);
      showSuccess('Invite resent successfully');
    } catch (error) {
      console.error('Failed to resend invite:', error);
      showError('Failed to resend invite');
    }
  };

  const handleRevokeInvite = async (inviteId: string) => {
    try {
      await adminInviteService.revokeInvite(inviteId);
      setAdminInvites(prev =>
        prev.map(invite =>
          invite.id === inviteId ? { ...invite, status: 'revoked' } : invite
        )
      );
      showSuccess('Invite revoked successfully');
    } catch (error) {
      console.error('Failed to revoke invite:', error);
      showError('Failed to revoke invite');
    }
  };

  const handleApproveAdmin = async (inviteId: string) => {
    try {
      const invite = adminInvites.find(inv => inv.id === inviteId);
      if (invite) {
        const newAdmin = await adminInviteService.acceptInvite(invite, currentUser?.email || 'System');
        const acceptedAt = Timestamp.now();

        setAdminUsers(prev => [newAdmin, ...prev]);
        setAdminInvites(prev =>
          prev.map(item =>
            item.id === inviteId ? { ...item, status: 'accepted', acceptedAt } : item
          )
        );

        showSuccess(`${invite.email} has been approved as admin. They can now sign up at the admin signup page.`);
      }
    } catch (error) {
      console.error('Failed to approve admin:', error);
      showError('Failed to approve admin');
    }
  };

  const handleToggleAdminStatus = async (adminId: string) => {
    try {
      const targetAdmin = adminUsers.find(adm => adm.id === adminId);
      if (!targetAdmin) {
        showError('Admin not found');
        return;
      }

      await adminInviteService.toggleAdminStatus(adminId, !targetAdmin.isActive);

      setAdminUsers(prev =>
        prev.map(item =>
          item.id === adminId ? { ...item, isActive: !item.isActive } : item
        )
      );

      const updatedAdmin = adminUsers.find(adm => adm.id === adminId);
      if (updatedAdmin) {
        showSuccess(
          `${updatedAdmin.email} admin status ${updatedAdmin.isActive ? 'deactivated' : 'activated'
          }`
        );
      }
    } catch (error) {
      console.error('Failed to update admin status:', error);
      showError('Failed to update admin status');
    }
  };

  const handleRemoveAdmin = async (adminId: string) => {
    try {
      await adminInviteService.removeAdminUser(adminId);
      const admin = adminUsers.find(adm => adm.id === adminId);
      setAdminUsers(prev => prev.filter(adm => adm.id !== adminId));

      if (admin) {
        showSuccess(`${admin.email} has been removed from admin privileges`);
      }
    } catch (error) {
      console.error('Failed to remove admin:', error);
      showError('Failed to remove admin');
    }
  };

  const settingsSections = [
    { id: 'general', name: 'General', icon: '⚙️' },
    { id: 'profile', name: 'Profile', icon: '👤' },
    { id: 'email', name: 'Email', icon: '📧' },
    { id: 'security', name: 'Security', icon: '🔒' },
    { id: 'notifications', name: 'Notifications', icon: '🔔' },
    { id: 'appearance', name: 'Appearance', icon: '🎨' },
    { id: 'api', name: 'API', icon: '🔌' },
    { id: 'plan-pricing', name: 'Plan Pricing', icon: '💰' },
    { id: 'admin-invites', name: 'Admin Invites', icon: '👥' },
  ];

  const renderGeneralSettings = () => (
    <div className="space-y-6">
      <div>
        <label className={`block text-sm font-medium mb-2 ${isDark ? 'text-gray-300' : 'text-gray-700'
          }`}>Site Name</label>
        <input
          type="text"
          value={settings.siteName}
          onChange={(e) => handleInputChange('siteName', e.target.value)}
          className={`w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-black focus:border-transparent ${isDark
            ? 'bg-gray-800 border-gray-600 text-white'
            : 'bg-white border-gray-300 text-gray-900'
            }`}
        />
      </div>

      <div>
        <label className={`block text-sm font-medium mb-2 ${isDark ? 'text-gray-300' : 'text-gray-700'
          }`}>Site Description</label>
        <textarea
          value={settings.siteDescription}
          onChange={(e) => handleInputChange('siteDescription', e.target.value)}
          rows={3}
          className={`w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-black focus:border-transparent ${isDark
            ? 'bg-gray-800 border-gray-600 text-white'
            : 'bg-white border-gray-300 text-gray-900'
            }`}
        />
      </div>

      <div>
        <label className={`block text-sm font-medium mb-2 ${isDark ? 'text-gray-300' : 'text-gray-700'
          }`}>Admin Email</label>
        <input
          type="email"
          value={settings.adminEmail}
          onChange={(e) => handleInputChange('adminEmail', e.target.value)}
          className={`w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-black focus:border-transparent ${isDark
            ? 'bg-gray-800 border-gray-600 text-white'
            : 'bg-white border-gray-300 text-gray-900'
            }`}
        />
      </div>


      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className={`block text-sm font-medium mb-2 ${isDark ? 'text-gray-300' : 'text-gray-700'
            }`}>Timezone</label>
          <select
            value={settings.timezone}
            onChange={(e) => handleInputChange('timezone', e.target.value)}
            className={`w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-black focus:border-transparent ${isDark
              ? 'bg-gray-800 border-gray-600 text-white'
              : 'bg-white border-gray-300 text-gray-900'
              }`}
          >
            <option value="UTC">UTC</option>
            <option value="America/New_York">Eastern Time</option>
            <option value="America/Chicago">Central Time</option>
            <option value="America/Denver">Mountain Time</option>
            <option value="America/Los_Angeles">Pacific Time</option>
          </select>
        </div>

        <div>
          <label className={`block text-sm font-medium mb-2 ${isDark ? 'text-gray-300' : 'text-gray-700'
            }`}>Language</label>
          <select
            value={settings.language}
            onChange={(e) => handleInputChange('language', e.target.value)}
            className={`w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-black focus:border-transparent ${isDark
              ? 'bg-gray-800 border-gray-600 text-white'
              : 'bg-white border-gray-300 text-gray-900'
              }`}
          >
            <option value="en">English</option>
            <option value="es">Spanish</option>
            <option value="fr">French</option>
            <option value="de">German</option>
          </select>
        </div>
      </div>
    </div>
  );

  const renderProfileSettings = () => (
    <div className="space-y-6">
      <div className="mb-6">
        <h3 className={`text-lg font-semibold mb-2 ${isDark ? 'text-white' : 'text-gray-900'}`}>
          Admin Profile Information
        </h3>
        <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
          Manage your admin profile picture and personal information.
        </p>
      </div>

      {/* Admin Profile Picture Upload */}
      <div>
        <label className={`block text-sm font-medium mb-2 ${isDark ? 'text-gray-300' : 'text-gray-700'
          }`}>Profile Picture</label>

        <div className="flex items-start space-x-6">
          {/* Profile Picture Preview */}
          <div className="flex-shrink-0">
            <div className={`w-24 h-24 rounded-full border-2 border-dashed flex items-center justify-center overflow-hidden ${isDark ? 'border-gray-600 bg-gray-800' : 'border-gray-300 bg-gray-50'
              }`}>
              {imagePreview || settings.adminProfilePicture ? (
                <img
                  src={imagePreview || settings.adminProfilePicture}
                  alt="Admin Profile"
                  className="w-full h-full object-cover rounded-full"
                />
              ) : (
                <div className={`text-3xl ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                  👤
                </div>
              )}
            </div>
          </div>

          {/* Upload Controls */}
          <div className="flex-1 space-y-4">
            <div className="flex items-center space-x-3">
              <label
                htmlFor="profile-picture-upload"
                className={`px-6 py-3 rounded-lg border cursor-pointer transition-colors duration-200 font-medium ${isUploadingImage
                  ? 'bg-gray-100 border-gray-300 text-gray-400 cursor-not-allowed'
                  : isDark
                    ? 'bg-gray-700 border-gray-600 text-white hover:bg-gray-600'
                    : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
                  }`}
              >
                {isUploadingImage ? 'Uploading...' : 'Choose Image'}
              </label>

              <input
                id="profile-picture-upload"
                type="file"
                accept="image/*"
                onChange={handleImageUpload}
                disabled={isUploadingImage}
                className="hidden"
              />

              {(imagePreview || settings.adminProfilePicture) && (
                <button
                  onClick={handleRemoveImage}
                  disabled={isUploadingImage}
                  className={`px-6 py-3 rounded-lg border transition-colors duration-200 font-medium ${isUploadingImage
                    ? 'bg-gray-100 border-gray-300 text-gray-400 cursor-not-allowed'
                    : isDark
                      ? 'bg-red-700 border-red-600 text-white hover:bg-red-600'
                      : 'bg-red-50 border-red-300 text-red-700 hover:bg-red-100'
                    }`}
                >
                  Remove
                </button>
              )}
            </div>

            <div className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
              <div className="space-y-1">
                <p>• <strong>Supported formats:</strong> JPG, PNG, GIF, WebP</p>
                <p>• <strong>Maximum file size:</strong> 5MB</p>
                <p>• <strong>Recommended size:</strong> 200x200px or larger</p>
                <p>• <strong>Best results:</strong> Square images work best for profile pictures</p>
              </div>
            </div>

            {isUploadingImage && (
              <div className="flex items-center space-x-3">
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-cyan-500"></div>
                <span className={`text-sm font-medium ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                  Uploading image...
                </span>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Admin Information Display */}
      <div className={`p-4 rounded-lg ${isDark ? 'bg-gray-800' : 'bg-gray-50'}`}>
        <h4 className={`text-sm font-medium mb-3 ${isDark ? 'text-white' : 'text-gray-900'}`}>
          Current Admin Information
        </h4>
        <div className="space-y-2">
          <div className="flex justify-between">
            <span className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Email:</span>
            <span className={`text-sm font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>
              {settings.adminEmail}
            </span>
          </div>
          <div className="flex justify-between">
            <span className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Role:</span>
            <span className={`text-sm font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>
              Administrator
            </span>
          </div>
          <div className="flex justify-between">
            <span className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Profile Picture:</span>
            <span className={`text-sm font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>
              {settings.adminProfilePicture ? 'Uploaded' : 'Not set'}
            </span>
          </div>
        </div>
      </div>
    </div>
  );

  const renderEmailSettings = () => (
    <div className="space-y-6">
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">SMTP Host</label>
          <input
            type="text"
            value={settings.smtpHost}
            onChange={(e) => handleInputChange('smtpHost', e.target.value)}
            placeholder="smtp.gmail.com"
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-black focus:border-transparent"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">SMTP Port</label>
          <input
            type="number"
            value={settings.smtpPort}
            onChange={(e) => handleInputChange('smtpPort', parseInt(e.target.value))}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-black focus:border-transparent"
          />
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">SMTP Username</label>
          <input
            type="text"
            value={settings.smtpUsername}
            onChange={(e) => handleInputChange('smtpUsername', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-black focus:border-transparent"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">SMTP Password</label>
          <input
            type="password"
            value={settings.smtpPassword}
            onChange={(e) => handleInputChange('smtpPassword', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-black focus:border-transparent"
          />
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">From Email</label>
          <input
            type="email"
            value={settings.fromEmail}
            onChange={(e) => handleInputChange('fromEmail', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-black focus:border-transparent"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">From Name</label>
          <input
            type="text"
            value={settings.fromName}
            onChange={(e) => handleInputChange('fromName', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-black focus:border-transparent"
          />
        </div>
      </div>
    </div>
  );

  const renderSecuritySettings = () => (
    <div className="space-y-6">
      <div className="bg-white p-6 rounded-lg border border-gray-200 shadow-sm">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="text-lg font-medium text-gray-900">Two-Factor Authentication (2FA)</h3>
            <p className="text-sm text-gray-500">Secure your account with Google Authenticator</p>
          </div>
          <div className="flex items-center">
            {currentUserHas2FA ? (
              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800 mr-4">
                Enabled
              </span>
            ) : (
              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800 mr-4">
                Disabled
              </span>
            )}

            {!currentUserHas2FA && !isSettingUp2FA && (
              <button
                onClick={initiate2FASetup}
                className="bg-black text-white px-4 py-2 rounded-md text-sm font-medium hover:bg-gray-800 transition-colors"
              >
                Enable 2FA
              </button>
            )}

            {currentUserHas2FA && (
              <button
                onClick={disable2FA}
                className="bg-white text-red-600 border border-red-200 px-4 py-2 rounded-md text-sm font-medium hover:bg-red-50 transition-colors"
              >
                Disable 2FA
              </button>
            )}
          </div>
        </div>

        {isSettingUp2FA && (
          <div className="mt-6 border-t pt-6">
            <h4 className="text-base font-medium text-gray-900 mb-4">Setup 2FA</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div className="flex flex-col items-center justify-center bg-gray-50 p-6 rounded-lg">
                {qrCodeDataUrl ? (
                  <img src={qrCodeDataUrl} alt="2FA QR Code" className="w-48 h-48" />
                ) : (
                  <div className="w-48 h-48 flex items-center justify-center">Loading QR...</div>
                )}
                <p className="mt-4 text-xs text-center text-gray-500 max-w-xs">
                  Scan this QR code with Google Authenticator or any TOTP app.
                </p>
              </div>

              <div className="flex flex-col justify-center space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Verification Code
                  </label>
                  <p className="text-xs text-gray-500 mb-3">
                    Enter the 6-digit code from your authenticator app
                  </p>
                  <input
                    type="text"
                    placeholder="000 000"
                    value={verificationCode}
                    onChange={(e) => setVerificationCode(e.target.value.replace(/[^0-9]/g, ''))}
                    maxLength={6}
                    className="block w-full border-gray-300 rounded-md shadow-sm focus:ring-black focus:border-black sm:text-lg tracking-widest text-center py-3"
                  />
                </div>

                {setupError && (
                  <div className="text-red-600 text-sm bg-red-50 p-2 rounded">
                    {setupError}
                  </div>
                )}

                <div className="flex space-x-3">
                  <button
                    onClick={() => setIsSettingUp2FA(false)}
                    className="flex-1 bg-white border border-gray-300 text-gray-700 px-4 py-2 rounded-md text-sm font-medium hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={finalize2FASetup}
                    disabled={verificationCode.length !== 6}
                    className="flex-1 bg-black text-white px-4 py-2 rounded-md text-sm font-medium hover:bg-gray-800 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Verify & Enable
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>


      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">Session Timeout (minutes)</label>
        <input
          type="number"
          value={settings.sessionTimeout}
          onChange={(e) => handleInputChange('sessionTimeout', parseInt(e.target.value))}
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-black focus:border-transparent"
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">Minimum Password Length</label>
        <input
          type="number"
          value={settings.passwordMinLength}
          onChange={(e) => handleInputChange('passwordMinLength', parseInt(e.target.value))}
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-black focus:border-transparent"
        />
      </div>

      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-medium text-gray-900">Require Strong Passwords</h3>
          <p className="text-sm text-gray-500">Enforce complex password requirements</p>
        </div>
        <button
          onClick={() => handleInputChange('requireStrongPasswords', !settings.requireStrongPasswords)}
          className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${settings.requireStrongPasswords ? 'bg-black' : 'bg-gray-200'
            }`}
        >
          <span
            className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${settings.requireStrongPasswords ? 'translate-x-6' : 'translate-x-1'
              }`}
          />
        </button>
      </div>
    </div>
  );

  const renderNotificationSettings = () => (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h3 className={`text-lg font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>Email Notifications</h3>
          <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Receive notifications via email</p>
        </div>
        <button
          onClick={() => handleInputChange('emailNotifications', !settings.emailNotifications)}
          className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${settings.emailNotifications ? 'bg-green-500' : 'bg-red-500'
            }`}
        >
          <span
            className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${settings.emailNotifications ? 'translate-x-6' : 'translate-x-1'
              }`}
          />
        </button>
      </div>

      <div className="flex items-center justify-between">
        <div>
          <h3 className={`text-lg font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>New User Signup Sound</h3>
          <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Play a sound when a new user signs up</p>
        </div>
        <button
          onClick={() => handleToggleSignupSound(!enableSignupSound)}
          className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${enableSignupSound ? 'bg-green-500' : 'bg-red-500'
            }`}
        >
          <span
            className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${enableSignupSound ? 'translate-x-6' : 'translate-x-1'
              }`}
          />
        </button>
      </div>


      <div className="flex items-center justify-between">
        <div>
          <h3 className={`text-lg font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>System Alerts</h3>
          <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Receive system alerts and warnings</p>
        </div>
        <button
          onClick={() => handleInputChange('systemAlerts', !settings.systemAlerts)}
          className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${settings.systemAlerts ? 'bg-green-500' : 'bg-red-500'
            }`}
        >
          <span
            className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${settings.systemAlerts ? 'translate-x-6' : 'translate-x-1'
              }`}
          />
        </button>
      </div>

      <div className="flex items-center justify-between">
        <div>
          <h3 className={`text-lg font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>Maintenance Mode</h3>
          <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Put the system in maintenance mode</p>
        </div>
        <button
          onClick={() => handleInputChange('maintenanceMode', !settings.maintenanceMode)}
          className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${settings.maintenanceMode ? 'bg-green-500' : 'bg-red-500'
            }`}
        >
          <span
            className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${settings.maintenanceMode ? 'translate-x-6' : 'translate-x-1'
              }`}
          />
        </button>
      </div>
    </div>
  );

  const renderAppearanceSettings = () => (
    <div className="space-y-6">
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">Theme</label>
        <select
          value={settings.theme}
          onChange={(e) => handleInputChange('theme', e.target.value)}
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-black focus:border-transparent"
        >
          <option value="light">Light</option>
          <option value="dark">Dark</option>
          <option value="auto">Auto</option>
        </select>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">Primary Color</label>
        <div className="flex items-center space-x-3">
          <input
            type="color"
            value={settings.primaryColor}
            onChange={(e) => handleInputChange('primaryColor', e.target.value)}
            className="h-10 w-20 border border-gray-300 rounded-md"
          />
          <input
            type="text"
            value={settings.primaryColor}
            onChange={(e) => handleInputChange('primaryColor', e.target.value)}
            className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-black focus:border-transparent"
          />
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">Logo URL</label>
        <input
          type="url"
          value={settings.logoUrl}
          onChange={(e) => handleInputChange('logoUrl', e.target.value)}
          placeholder="https://example.com/logo.png"
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-black focus:border-transparent"
        />
      </div>
    </div>
  );

  const handlePricingTierChange = (
    plan: 'pro' | 'business',
    index: number,
    field: 'subscribers' | 'price',
    value: number
  ) => {
    setPlanPricing(prev => ({
      ...prev,
      [plan]: prev[plan].map((tier, i) =>
        i === index ? { ...tier, [field]: value } : tier
      )
    }));
  };

  const handleAddPricingTier = (plan: 'pro' | 'business') => {
    const tiers = planPricing[plan];
    const lastTier = tiers[tiers.length - 1];
    const newTier: PricingTier = {
      subscribers: lastTier ? lastTier.subscribers + 1000 : 1000,
      price: lastTier ? lastTier.price + 10 : 10
    };
    setPlanPricing(prev => ({
      ...prev,
      [plan]: [...prev[plan], newTier]
    }));
  };

  const handleRemovePricingTier = (plan: 'pro' | 'business', index: number) => {
    if (planPricing[plan].length <= 1) {
      showError('At least one pricing tier is required');
      return;
    }
    setPlanPricing(prev => ({
      ...prev,
      [plan]: prev[plan].filter((_, i) => i !== index)
    }));
  };

  const handleSavePlanPricing = async () => {
    setIsSavingPricing(true);
    try {
      await planPricingService.updatePlanPricing(
        {
          pro: { tiers: planPricing.pro },
          business: { tiers: planPricing.business }
        },
        currentUser?.email || 'unknown'
      );
      showSuccess('Plan pricing updated successfully!');
    } catch (error) {
      console.error('Failed to save plan pricing:', error);
      showError('Failed to save plan pricing');
    } finally {
      setIsSavingPricing(false);
    }
  };

  const handleResetPlanPricing = async () => {
    if (!window.confirm('Are you sure you want to reset all pricing to defaults? This cannot be undone.')) {
      return;
    }
    setIsSavingPricing(true);
    try {
      await planPricingService.resetToDefaults(currentUser?.email || 'unknown');
      const pricing = await planPricingService.getPlanPricing();
      setPlanPricing({
        pro: pricing.pro.tiers,
        business: pricing.business.tiers
      });
      showSuccess('Plan pricing reset to defaults');
    } catch (error) {
      console.error('Failed to reset plan pricing:', error);
      showError('Failed to reset plan pricing');
    } finally {
      setIsSavingPricing(false);
    }
  };

  const renderPlanPricingSettings = () => (
    <div className="space-y-6">
      <div className="mb-6">
        <h3 className={`text-lg font-semibold mb-2 ${isDark ? 'text-white' : 'text-gray-900'}`}>
          Plan Pricing Management
        </h3>
        <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
          Set the pricing for Pro and Business plans based on subscriber counts. Changes will be reflected throughout the application.
        </p>
      </div>

      {isLoadingPricing ? (
        <div className="text-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-black mx-auto"></div>
          <p className={`mt-4 text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Loading pricing...</p>
        </div>
      ) : (
        <>
          {/* Pro Plan Pricing */}
          <div className={`rounded-lg border p-6 ${isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}>
            <div className="flex items-center justify-between mb-4">
              <h4 className={`text-lg font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>Pro Plan Pricing</h4>
              <button
                onClick={() => handleAddPricingTier('pro')}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${isDark
                  ? 'bg-gray-700 text-white hover:bg-gray-600'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
              >
                + Add Tier
              </button>
            </div>
            <div className="space-y-3">
              {planPricing.pro.map((tier, index) => (
                <div key={index} className="flex items-center gap-4 p-3 rounded-lg bg-gray-50 dark:bg-gray-900">
                  <div className="flex-1">
                    <label className={`block text-xs font-medium mb-1 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                      Subscribers
                    </label>
                    <input
                      type="number"
                      value={tier.subscribers}
                      onChange={(e) => handlePricingTierChange('pro', index, 'subscribers', parseInt(e.target.value) || 0)}
                      className={`w-full px-3 py-2 border rounded-md text-sm ${isDark
                        ? 'bg-gray-800 border-gray-600 text-white'
                        : 'bg-white border-gray-300 text-gray-900'
                        }`}
                    />
                  </div>
                  <div className="flex-1">
                    <label className={`block text-xs font-medium mb-1 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                      Price (USD)
                    </label>
                    <input
                      type="number"
                      step="0.01"
                      value={tier.price}
                      onChange={(e) => handlePricingTierChange('pro', index, 'price', parseFloat(e.target.value) || 0)}
                      className={`w-full px-3 py-2 border rounded-md text-sm ${isDark
                        ? 'bg-gray-800 border-gray-600 text-white'
                        : 'bg-white border-gray-300 text-gray-900'
                        }`}
                    />
                  </div>
                  <button
                    onClick={() => handleRemovePricingTier('pro', index)}
                    disabled={planPricing.pro.length <= 1}
                    className={`px-3 py-2 rounded-md text-sm font-medium transition-colors ${planPricing.pro.length <= 1
                      ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                      : isDark
                        ? 'bg-red-700 text-white hover:bg-red-600'
                        : 'bg-red-100 text-red-700 hover:bg-red-200'
                      }`}
                  >
                    Remove
                  </button>
                </div>
              ))}
            </div>
          </div>

          {/* Business Plan Pricing */}
          <div className={`rounded-lg border p-6 ${isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}>
            <div className="flex items-center justify-between mb-4">
              <h4 className={`text-lg font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>Business Plan Pricing</h4>
              <button
                onClick={() => handleAddPricingTier('business')}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${isDark
                  ? 'bg-gray-700 text-white hover:bg-gray-600'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
              >
                + Add Tier
              </button>
            </div>
            <div className="space-y-3">
              {planPricing.business.map((tier, index) => (
                <div key={index} className="flex items-center gap-4 p-3 rounded-lg bg-gray-50 dark:bg-gray-900">
                  <div className="flex-1">
                    <label className={`block text-xs font-medium mb-1 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                      Subscribers
                    </label>
                    <input
                      type="number"
                      value={tier.subscribers}
                      onChange={(e) => handlePricingTierChange('business', index, 'subscribers', parseInt(e.target.value) || 0)}
                      className={`w-full px-3 py-2 border rounded-md text-sm ${isDark
                        ? 'bg-gray-800 border-gray-600 text-white'
                        : 'bg-white border-gray-300 text-gray-900'
                        }`}
                    />
                  </div>
                  <div className="flex-1">
                    <label className={`block text-xs font-medium mb-1 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                      Price (USD)
                    </label>
                    <input
                      type="number"
                      step="0.01"
                      value={tier.price}
                      onChange={(e) => handlePricingTierChange('business', index, 'price', parseFloat(e.target.value) || 0)}
                      className={`w-full px-3 py-2 border rounded-md text-sm ${isDark
                        ? 'bg-gray-800 border-gray-600 text-white'
                        : 'bg-white border-gray-300 text-gray-900'
                        }`}
                    />
                  </div>
                  <button
                    onClick={() => handleRemovePricingTier('business', index)}
                    disabled={planPricing.business.length <= 1}
                    className={`px-3 py-2 rounded-md text-sm font-medium transition-colors ${planPricing.business.length <= 1
                      ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                      : isDark
                        ? 'bg-red-700 text-white hover:bg-red-600'
                        : 'bg-red-100 text-red-700 hover:bg-red-200'
                      }`}
                  >
                    Remove
                  </button>
                </div>
              ))}
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex items-center justify-between pt-4 border-t border-gray-200 dark:border-gray-700">
            <button
              onClick={handleResetPlanPricing}
              disabled={isSavingPricing}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${isSavingPricing
                ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                : isDark
                  ? 'bg-gray-700 text-white hover:bg-gray-600'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
            >
              Reset to Defaults
            </button>
            <button
              onClick={handleSavePlanPricing}
              disabled={isSavingPricing}
              className={`px-6 py-2 rounded-lg text-sm font-medium transition-colors ${isSavingPricing
                ? 'bg-gray-400 text-white cursor-not-allowed'
                : isDark
                  ? 'bg-blue-600 text-white hover:bg-blue-700'
                  : 'bg-black text-white hover:bg-gray-800'
                }`}
            >
              {isSavingPricing ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </>
      )}
    </div>
  );

  const renderApiSettings = () => (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-medium text-gray-900">API Access</h3>
          <p className="text-sm text-gray-500">Enable API access for third-party integrations</p>
        </div>
        <button
          onClick={() => handleInputChange('apiEnabled', !settings.apiEnabled)}
          className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${settings.apiEnabled ? 'bg-black' : 'bg-gray-200'
            }`}
        >
          <span
            className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${settings.apiEnabled ? 'translate-x-6' : 'translate-x-1'
              }`}
          />
        </button>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">API Rate Limit (requests per hour)</label>
        <input
          type="number"
          value={settings.apiRateLimit}
          onChange={(e) => handleInputChange('apiRateLimit', parseInt(e.target.value))}
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-black focus:border-transparent"
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">Webhook URL</label>
        <input
          type="url"
          value={settings.webhookUrl}
          onChange={(e) => handleInputChange('webhookUrl', e.target.value)}
          placeholder="https://example.com/webhook"
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-black focus:border-transparent"
        />
      </div>
    </div>
  );

  const renderAdminInvitesSettings = () => (
    <div className="space-y-6">
      {/* Invite New Admin */}
      <div className="bg-gray-50 rounded-lg p-6">
        <h3 className="text-lg font-medium text-gray-900 mb-4">Invite New Admin</h3>
        <div className="flex space-x-4">
          <div className="flex-1">
            <label className="block text-sm font-medium text-gray-700 mb-2">Email Address</label>
            <input
              type="email"
              value={inviteEmail}
              onChange={(e) => setInviteEmail(e.target.value)}
              placeholder="Enter email address to invite as admin"
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-black focus:border-transparent"
            />
          </div>
          <div className="flex items-end">
            <button
              onClick={handleInviteAdmin}
              disabled={isInviting || isSendingInviteEmail || !inviteEmail.trim()}
              className="bg-black text-white px-6 py-2 rounded-lg hover:bg-gray-800 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
            >
              {isInviting || isSendingInviteEmail ? 'Sending...' : 'Send Invite'}
            </button>
          </div>
        </div>
        <p className="text-sm text-gray-500 mt-2">
          The invited person will receive an email with instructions to create an admin account.
        </p>

        {showEmailPreview && lastInvite && (
          <div className="mt-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
            <div className="flex justify-between items-center mb-2">
              <h4 className="text-sm font-medium text-blue-900">Email Preview</h4>
              <button
                onClick={() => setShowEmailPreview(false)}
                className="text-blue-600 hover:text-blue-800 text-sm"
              >
                Close Preview
              </button>
            </div>
            <p className="text-xs text-blue-700 mb-3">
              This is what the invitation email would look like:
            </p>
            <div className="max-h-96 overflow-y-auto">
              <AdminInviteEmail
                inviteEmail={lastInvite.email}
                inviteToken={lastInvite.inviteToken || ''}
                invitedBy={lastInvite.invitedBy}
              />
            </div>
          </div>
        )}
      </div>

      {/* Admin Invites List */}
      <div>
        <h3 className="text-lg font-medium text-gray-900 mb-4">Admin Invites</h3>
        {loadingAdminData ? (
          <div className="text-center py-6 text-sm text-gray-500">
            Loading admin invites...
          </div>
        ) : adminInvites.length === 0 ? (
          <div className="text-center py-8">
            <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
            </svg>
            <h3 className="mt-2 text-sm font-medium text-gray-900">No admin invites</h3>
            <p className="mt-1 text-sm text-gray-500">Get started by inviting someone to become an admin.</p>
          </div>
        ) : (
          <div className="space-y-3">
            {adminInvites.map((invite) => (
              <div key={invite.id} className={`border rounded-lg p-4 ${isDark
                ? 'bg-gray-800 border-gray-700'
                : 'bg-white border-gray-200'
                }`}>
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <div className="flex items-center space-x-3">
                      <div className="flex-shrink-0">
                        <div className="w-8 h-8 bg-gray-100 rounded-full flex items-center justify-center">
                          <svg className="w-4 h-4 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                          </svg>
                        </div>
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-900">{invite.email}</p>
                        <div className="flex items-center space-x-4 text-xs text-gray-500">
                          <span>Invited by: {invite.invitedBy}</span>
                          <span>•</span>
                          <span>Invited: {formatTimestamp(invite.invitedAt)}</span>
                          <span>•</span>
                          <span>Expires: {formatTimestamp(invite.expiresAt)}</span>
                        </div>
                      </div>
                      <div className="flex items-center space-x-2">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${invite.status === 'pending'
                          ? 'bg-yellow-100 text-yellow-800'
                          : invite.status === 'accepted'
                            ? 'bg-green-100 text-green-800'
                            : invite.status === 'revoked'
                              ? 'bg-red-100 text-red-800'
                              : 'bg-gray-100 text-gray-600'
                          }`}>
                          {invite.status === 'pending'
                            ? 'Pending'
                            : invite.status === 'accepted'
                              ? 'Accepted'
                              : invite.status === 'revoked'
                                ? 'Revoked'
                                : 'Expired'}
                        </span>
                        {invite.status === 'pending' && (
                          <div className="flex space-x-1">
                            <button
                              onClick={() => handleApproveAdmin(invite.id)}
                              className="text-gray-400 hover:text-green-600 p-1"
                              title="Approve as admin"
                            >
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                              </svg>
                            </button>
                            <button
                              onClick={() => handleResendInvite(invite.id)}
                              className="text-gray-400 hover:text-gray-600 p-1"
                              title="Resend invite"
                            >
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                              </svg>
                            </button>
                            <button
                              onClick={() => handleRevokeInvite(invite.id)}
                              className="text-gray-400 hover:text-red-600 p-1"
                              title="Revoke invite"
                            >
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                              </svg>
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Admin Users Management */}
      <div>
        <h3 className="text-lg font-medium text-gray-900 mb-4">Admin Users</h3>
        {loadingAdminData ? (
          <div className="text-center py-6 text-sm text-gray-500">
            Loading admin users...
          </div>
        ) : adminUsers.length === 0 ? (
          <div className="text-center py-8">
            <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5-9a2.5 2.5 0 11-5 0 2.5 2.5 0 015 0z" />
            </svg>
            <h3 className="mt-2 text-sm font-medium text-gray-900">No admin users</h3>
            <p className="mt-1 text-sm text-gray-500">Approved admins will appear here.</p>
          </div>
        ) : (
          <div className="space-y-3">
            {adminUsers.map((admin) => (
              <div key={admin.id} className={`border rounded-lg p-4 ${isDark
                ? 'bg-gray-800 border-gray-700'
                : 'bg-white border-gray-200'
                }`}>
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <div className="flex items-center space-x-3">
                      <div className="flex-shrink-0">
                        <div className="w-8 h-8 bg-gray-100 rounded-full flex items-center justify-center">
                          <svg className="w-4 h-4 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                          </svg>
                        </div>
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-900">{admin.email}</p>
                        <div className="flex items-center space-x-4 text-xs text-gray-500">
                          <span>Added by: {admin.addedBy}</span>
                          <span>•</span>
                          <span>Added: {formatTimestamp(admin.addedAt)}</span>
                        </div>
                      </div>
                      <div className="flex items-center space-x-3">
                        <div className="flex items-center space-x-2">
                          <span className="text-sm text-gray-600">Active</span>
                          <button
                            onClick={() => handleToggleAdminStatus(admin.id)}
                            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${admin.isActive ? 'bg-green-600' : 'bg-gray-200'
                              }`}
                          >
                            <span
                              className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${admin.isActive ? 'translate-x-6' : 'translate-x-1'
                                }`}
                            />
                          </button>
                        </div>
                        <button
                          onClick={() => handleRemoveAdmin(admin.id)}
                          className="text-gray-400 hover:text-red-600 p-1"
                          title="Remove admin"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                          </svg>
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );

  const renderSectionContent = () => {
    switch (activeSection) {
      case 'general':
        return renderGeneralSettings();
      case 'profile':
        return renderProfileSettings();
      case 'email':
        return renderEmailSettings();
      case 'security':
        return renderSecuritySettings();
      case 'notifications':
        return renderNotificationSettings();
      case 'appearance':
        return renderAppearanceSettings();
      case 'api':
        return renderApiSettings();
      case 'plan-pricing':
        return renderPlanPricingSettings();
      case 'admin-invites':
        return renderAdminInvitesSettings();
      default:
        return renderGeneralSettings();
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>Settings</h2>
        <button
          onClick={handleSave}
          disabled={isSaving}
          className={`px-6 py-2 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200 ${isDark
            ? 'bg-blue-600 text-white hover:bg-blue-700'
            : 'bg-black text-white hover:bg-gray-800'
            }`}
        >
          {isSaving ? 'Saving...' : 'Save Changes'}
        </button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        {/* Settings Navigation */}
        <div className="lg:col-span-1">
          <nav className="space-y-2">
            {settingsSections.map((section) => (
              <button
                key={section.id}
                onClick={() => setActiveSection(section.id)}
                className={`w-full flex items-center space-x-3 px-4 py-3 rounded-lg text-left transition-colors duration-200 ${activeSection === section.id
                  ? isDark
                    ? 'bg-gray-700 text-white border border-gray-600'
                    : 'bg-black text-white'
                  : isDark
                    ? 'text-gray-300 hover:bg-gray-800'
                    : 'text-gray-700 hover:bg-gray-100'
                  }`}
              >
                <span className="text-lg">{section.icon}</span>
                <span className="font-medium">{section.name}</span>
              </button>
            ))}
          </nav>
        </div>

        {/* Settings Content */}
        <div className="lg:col-span-3">
          <div className={`rounded-lg shadow-sm border p-6 ${isDark
            ? 'bg-gray-900 border-gray-700'
            : 'bg-white border-gray-200'
            }`}>
            {renderSectionContent()}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminSettings;
